// src/features/Forecast/ForecastPage.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Chip,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  TrendingUp,
  ShowChart,
  Timeline,
  Download,
  Insights,
  WaterDrop,
  Refresh,
  Error,
  Psychology
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { getData } from '../../services/storageService';
import useMLPredictionsTF from '../Analytics/hooks/useMLPredictionsTF';
import { useResourcePredictions } from '../Analytics/hooks/useResourcePredictions';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
  Legend 
} from 'recharts';

// Компонент статусу ML моделі з TensorFlow підтримкою
const MLStatusIndicator = ({ modelState, modelInfo, onRefresh }) => {
  const { t } = useI18n();
  const theme = useTheme();
  
  // Визначаємо конфігурацію моделі
  const getModelConfig = () => {
    if (modelInfo.usingTensorFlow) {
      return {
        color: 'success',
        icon: <Psychology fontSize="small" />,
        label: 'TensorFlow.js',
        description: t('ml_tf_advanced_features')
      };
    } else if (modelInfo.type === 'linear_regression') {
      return {
        color: 'primary',
        icon: <TrendingUp fontSize="small" />,
        label: t('ml_model_linear'),
        description: t('ml_model_linear')
      };
    } else {
      return {
        color: 'default',
        icon: <ShowChart fontSize="small" />,
        label: t('ml_model_fallback'),
        description: t('ml_status_fallback')
      };
    }
  };

  const modelConfig = getModelConfig();

  if (modelState.isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          {modelInfo.usingTensorFlow ? t('ml_tf_model_training') : t('ml_status_training')}
        </Typography>
      </Box>
    );
  }
  
  if (modelState.error) {
    return (
      <Tooltip title={modelState.error}>
        <Chip 
          icon={<Error fontSize="small" />}
          label={t('ml_status_error')}
          color="error"
          size="small"
          onClick={onRefresh}
          clickable
        />
      </Tooltip>
    );
  }
  
  if (modelState.isTrained) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Інформація про тип моделі */}
        <Tooltip title={modelConfig.description}>
          <Chip 
            icon={modelConfig.icon}
            label={modelConfig.label}
            color={modelConfig.color}
            size="small"
            variant="outlined"
          />
        </Tooltip>
        
        {/* Точність моделі */}
        <Chip 
          label={t('ml_status_trained', { accuracy: Math.round(modelInfo.accuracy) })}
          color={modelInfo.accuracy > 80 ? 'success' : modelInfo.accuracy > 60 ? 'warning' : 'default'}
          size="small"
          variant="filled"
        />

        {/* Якість даних */}
        <Tooltip title={t('ml_data_quality') + `: ${modelInfo.dataQuality}%`}>
          <Chip 
            label={`${modelInfo.dataQuality}%`}
            color={modelInfo.dataQuality > 80 ? 'success' : modelInfo.dataQuality > 60 ? 'warning' : 'error'}
            size="small"
            variant="outlined"
          />
        </Tooltip>
        
        {/* Кнопка оновлення */}
        <IconButton size="small" onClick={onRefresh} color="primary">
          <Refresh />
        </IconButton>
      </Stack>
    );
  }
  
  return (
    <Chip 
      icon={<Psychology fontSize="small" />}
      label={t('ml_status_fallback')}
      color="default"
      size="small"
      variant="outlined"
    />
  );
};

// Компонент прогнозу виробництва
const ProductionForecast = ({ period = 7 }) => {
  const { t } = useI18n();
  const theme = useTheme();
  
  // Використання ML-хука з TensorFlow
  const { 
    predictions, 
    predictionStats,
    modelState,
    modelInfo,
    refreshPredictions 
  } = useMLPredictionsTF({
    predictionDays: period,
    autoRefresh: true,
    useTensorFlow: true
  });

  // Дані для графіка
  const chartData = useMemo(() => {
    if (predictions.length === 0) {
      // Резервні дані, якщо прогнози недоступні
      return Array.from({ length: period }, (_, i) => ({
        day: `${t('common_days')} ${i + 1}`,
        forecast: 1500 + Math.random() * 500,
        confidence: 80 - i * 5,
        isPrediction: false
      }));
    }

    return predictions.map(pred => ({
      day: new Date(pred.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }),
      forecast: pred.predictedProduction,
      confidence: pred.confidence,
      date: pred.date,
      isPrediction: true,
      modelUsed: pred.modelUsed
    }));
  }, [predictions, period, t]);

  return (
    <Card>
      <CardContent>
        {/* Заголовок з індикатором стану ML */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('forecast_production_title')}
          </Typography>
          
          <MLStatusIndicator 
            modelState={modelState}
            modelInfo={modelInfo}
            onRefresh={refreshPredictions}
          />
        </Box>

        {/* Графік прогнозів */}
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
            <XAxis dataKey="day" />
            <YAxis />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'forecast') return [`${value.toFixed(0)} ${t('labels_kg')}`, t('forecast_production')];
                if (name === 'confidence') return [`${value}%`, t('forecast_accuracy')];
                return [value, name];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) {
                  const item = payload[0].payload;
                  return `${item.date} (${item.modelUsed || 'ML'})`;
                }
                return label;
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="forecast" 
              stroke={theme.palette.primary.main}
              strokeWidth={3}
              dot={{ fill: theme.palette.primary.main, r: 4 }}
              name={t('forecast_production')}
            />
          </LineChart>
        </ResponsiveContainer>
        
        {/* Статистика прогнозів */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('forecast_average')}:
            </Typography>
            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
              {predictionStats.averageProduction} {t('labels_kg')}/{t('common_days').toLowerCase()}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('forecast_accuracy')}:
            </Typography>
            <Typography variant="h6" color={predictionStats.confidence > 70 ? 'success.main' : 'warning.main'} sx={{ fontWeight: 600 }}>
              {predictionStats.confidence}%
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('analytics_trend_label')}:
            </Typography>
            <Chip 
              label={predictionStats.trend === 'up' ? t('trend_up') : 
                     predictionStats.trend === 'down' ? t('trend_down') : t('trend_stable')}
              color={predictionStats.trend === 'up' ? 'success' : 
                     predictionStats.trend === 'down' ? 'error' : 'default'}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('ml_model_label')}:
            </Typography>
            <Chip 
              label={modelInfo.usingTensorFlow ? t('ml_tf_using_tensorflow') : 
                     modelInfo.type === 'linear_regression' ? t('ml_model_linear') : t('ml_model_fallback')}
              color={modelInfo.usingTensorFlow ? 'success' : 
                     modelInfo.type === 'linear_regression' ? 'primary' : 'default'}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>

        {/* Інформація про якість даних */}
        {modelInfo.dataQuality < 80 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('ml_data_quality')}: {modelInfo.dataQuality}%. {t('forecast_data_quality_warning')}
            </Typography>
          </Alert>
        )}

        {/* Інформація про TensorFlow */}
        {modelInfo.usingTensorFlow && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              🚀 {t('ml_tf_high_accuracy')} - {t('ml_tf_advanced_features')}
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Компонент прогнозу споживання (залишається без змін)
const ConsumptionForecast = ({ period = 7 }) => {
  const { t } = useI18n();
  const theme = useTheme();
  
  // Отримуємо прогнози виробництва для ресурсів
  const { predictions: productionPredictions } = useMLPredictionsTF({
    predictionDays: period,
    autoRefresh: false
  });
  
  // Прогнози ресурсів на основі виробництва
  const { resourcePredictions, modelState } = useResourcePredictions(productionPredictions);

  // Дані для графіка
  const chartData = useMemo(() => {
    if (resourcePredictions.length === 0) {
      // Резервні дані
      return Array.from({ length: period }, (_, i) => ({
        day: `${t('common_days')} ${i + 1}`,
        water: 8000 + Math.random() * 2000,
        propane: 500 + Math.random() * 200,
        electricity: 1200 + Math.random() * 300
      }));
    }

    return resourcePredictions.map(pred => ({
      day: new Date(pred.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }),
      water: pred.water,
      propane: pred.propane,
      electricity: pred.electricity,
      date: pred.date,
      production: pred.predictedProduction,
      confidence: pred.confidence
    }));
  }, [resourcePredictions, period, t]);

  // Розраховуємо ефективність
  const efficiencyStats = useMemo(() => {
    if (resourcePredictions.length === 0) return null;

    const avgWaterEfficiency = resourcePredictions.reduce((sum, pred) => {
      return sum + (pred.water > 0 ? pred.predictedProduction / pred.water : 0);
    }, 0) / resourcePredictions.length;

    const avgPropaneEfficiency = resourcePredictions.reduce((sum, pred) => {
      return sum + (pred.propane > 0 ? pred.predictedProduction / pred.propane : 0);
    }, 0) / resourcePredictions.length;

    return {
      waterEfficiency: avgWaterEfficiency.toFixed(2),
      propaneEfficiency: avgPropaneEfficiency.toFixed(2),
      avgWaterConsumption: Math.round(resourcePredictions.reduce((sum, pred) => sum + pred.water, 0) / resourcePredictions.length),
      avgPropaneConsumption: Math.round(resourcePredictions.reduce((sum, pred) => sum + pred.propane, 0) / resourcePredictions.length)
    };
  }, [resourcePredictions]);

  return (
    <Card>
      <CardContent>
        {/* Заголовок з статусом ML */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('resource_forecast_title')}
          </Typography>
          
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip 
              label={`${t('resource_water')}: ${modelState.waterTrained ? 'ML' : t('ml_status_fallback')}`}
              color={modelState.waterTrained ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
            <Chip 
              label={`${t('resource_propane')}: ${modelState.propaneTrained ? 'ML' : t('ml_status_fallback')}`}
              color={modelState.propaneTrained ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Графік споживання */}
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
            <XAxis dataKey="day" />
            <YAxis />
            <RechartsTooltip 
              formatter={(value, name) => {
                const units = {
                  water: t('consumption_units_water'),
                  propane: t('consumption_units_propane'),
                  electricity: t('consumption_units_electricity')
                };
                return [`${value} ${units[name] || ''}`, t(`resource_${name}`)];
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="water" 
              stroke={theme.palette.info.main}
              strokeWidth={2}
              name={t('resource_water')}
            />
            <Line 
              type="monotone" 
              dataKey="propane" 
              stroke={theme.palette.warning.main}
              strokeWidth={2}
              name={t('resource_propane')}
            />
            <Line 
              type="monotone" 
              dataKey="electricity" 
              stroke={theme.palette.success.main}
              strokeWidth={2}
              name={t('resource_electricity')}
            />
          </LineChart>
        </ResponsiveContainer>
        
        {/* Статистика ефективності */}
        {efficiencyStats && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('water_efficiency')}:
              </Typography>
              <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                {efficiencyStats.waterEfficiency} {t('efficiency_metric')}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('propane_efficiency')}:
              </Typography>
              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                {efficiencyStats.propaneEfficiency} {t('efficiency_metric')}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('resource_water')} ({t('consumption_units_water')}):
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {efficiencyStats.avgWaterConsumption}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('resource_propane')} ({t('consumption_units_propane')}):
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {efficiencyStats.avgPropaneConsumption}
              </Typography>
            </Grid>
          </Grid>
        )}

        {/* Сповіщення про якість даних */}
        {(!modelState.waterTrained || !modelState.propaneTrained) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('forecast_data_quality_warning')} {t('resource_consumption')}.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Головний компонент ForecastPage
function ForecastPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [tabIndex, setTabIndex] = useState(0);
  const [forecastPeriod, setForecastPeriod] = useState(7);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const allRecords = await getData('lainsa_records');
        setRecords(Array.isArray(allRecords) ? allRecords : []);
      } catch (error) {
        console.error('Error loading forecast data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
          }}
        >
          <TrendingUp sx={{ color: 'white', fontSize: 32 }} />
        </Box>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
            {t('forecast_title')}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {t('forecast_subtitle')}
          </Typography>
        </Box>
      </Stack>

      {/* Контролі */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('forecast_period')}</InputLabel>
              <Select
                value={forecastPeriod}
                onChange={(e) => setForecastPeriod(e.target.value)}
                label={t('forecast_period')}
              >
                <MenuItem value={7}>{t('forecast_7days')}</MenuItem>
                <MenuItem value={14}>{t('forecast_14days')}</MenuItem>
                <MenuItem value={30}>{t('forecast_30days')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<Download />} variant="outlined">
                {t('forecast_export')}
              </Button>
              <Button startIcon={<Insights />} variant="contained">
                {t('forecast_scenarios')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Вкладки */}
      <Card sx={{ mb: 4 }}>
        <Tabs 
          value={tabIndex} 
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<ShowChart />} label={t('forecast_production_title')} />
          <Tab icon={<WaterDrop />} label={t('forecast_consumption_title')} />
          <Tab icon={<Timeline />} label={t('forecast_scenarios_title')} />
        </Tabs>
      </Card>

      {/* Контент вкладок */}
      {tabIndex === 0 && (
        <ProductionForecast period={forecastPeriod} />
      )}
      
      {tabIndex === 1 && (
        <ConsumptionForecast period={forecastPeriod} />
      )}
      
      {tabIndex === 2 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Timeline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
              {t('forecast_development')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('forecast_scenarios_desc')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default ForecastPage;